import { SDK } from '../SDK';
import { PrivateAircraftData } from '../types/AircraftData';
import { PrivateTrackData } from '../types/TrackData';
import { HistoryTableTracksAPIResponse, TracksAPIResponse } from '../types/TracksAPIResponse';
import { KMLData } from '../types/KMLData';
import { AircraftAPIResponse } from '../types/AircraftAPIResponse';
import { HistoryTableData, TrackHistoryData } from '../types/TrackHistoryData';
import { KMLAPIResponse } from '../types/KMLAPIResponse';
import { UserData, UserPreference, UserPreferenceKey } from '../types/UserData';
import { DownloadTracksRequest } from '../types/DownloadTracksRequest';
import { TrackHistoryRequest } from '../types/TrackHistoryRequest';
import { UsersAPIResponse } from '../types/UsersAPIResponse';
import { FavouritesService } from './services/FavouritesService';
import { SpiderSettingsService } from './services/SpiderSettingsService';
import { GeofencesService } from './services/GeofencesService';
import { AircraftService } from './services/AircraftService';
import fileDownload from 'js-file-download';
import { FlightDataService } from './services/FlightDataService';
import { FlightData3DFRService } from './services/FlightData3DFRService';
import { SisenseService } from './services/SisenseService';
import { FlightEventConfigurationsService } from './services/FlightEventConfigurationsService';
import { EventRulesService } from './services/EventRulesService';
import { NotificationConfigurationService } from './services/NotificationConfigurationService';
import { EventService } from './services/EventService';
import { FeatureConfigService } from './services/FeatureConfigService';
import { IntegrationService } from './services/IntegrationService';
import { FeatureFlagsAPIResponse } from '../types/FeatureFlagsAPIResponse';
import { Person } from '../types/Person';
import { Member, MembersAPIResponse } from '../types/Member';
import { PersonAPIResponse } from '../types/PersonAPIResponse';
import { PersonAssociatedTag } from '../types/PersonAssociatedTag';
import { PersonAssociatedTagAPIResponse } from '../types/PersonAssociatedTagAPIResponse';
import { SavePersonAssociatedTagParams } from '../types/SavePersonAssociatedTagParams';
import { TrackTotalsResponse } from '../types/TrackTotalsResponse';
import { EventsExplorerService } from './services/EventsExplorerService';
import { InsightRulesService } from './services/InsightRulesService';
import { UserService } from './services/UserService';
import { ContactService } from './services/spidertxt/contactService';
import { ConversationService } from './services/spidertxt/conversationService';
import { POILocation } from '../../../../types/Track';
import { LocationCodeStandard } from '../types/Organisation';

export class PrivateAPI extends SDK {

  protected isPublic: boolean = false;

  protected pathSegments = {
    TRACKS: '/tracks',
    TRACKS_V2: '/v2/tracks',
    AIRCRAFT: '/users/aircraft',
    KML: '/kmls',
    KML_STATE: '/kmls/activeState/',
    USER: '/users',
    USER_PREFERENCES: '/users/states?impersonateEmail=',
    ORGANISATION: '/',
    ORG_AIRCRAFT: '/orgs/<ORGANISATION_ID>/aircraft',
    FEATURES: '/features',
    PERSONS: '/organisations/<ORGANISATION_ID>/persons',
    USER_TAGS: '/users/tags',
    MEMBERS: '/orgs/<ORGANISATION_ID>/members'
  };

  private addAuthToRequest(request: Request): Request {
    const newHeaders = new Headers(request.headers);
    newHeaders.append('Authorization', `${window.localStorage.hash}`);

    return new Request(request, {
      headers: newHeaders
    });
  }

  private getAircraftRequest(): Request {
    const request = new Request(this.buildUrl(this.getPath('AIRCRAFT')), {});

    return this.addAuthToRequest(request);
  }

  public async getAircraft(): Promise<PrivateAircraftData[]> {
    try {
      const aircraftData = await this.call<AircraftAPIResponse<PrivateAircraftData[]>>(
        this.getAircraftRequest()
      );

      return aircraftData.items;
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return [];
      } else {
        throw error;
      }
    }
  }

  private getOrgAircraftRequest(orgId: string): Request {
    const request = new Request(
      this.buildUrl(this.getPath('ORG_AIRCRAFT').replace('<ORGANISATION_ID>', orgId)),
      {}
    );

    return this.addAuthToRequest(request);
  }

  public async getOrgAircraft(orgId: string): Promise<PrivateAircraftData[]> {
    try {
      const aircraftData = await this.call<AircraftAPIResponse<PrivateAircraftData[]>>(
        this.getOrgAircraftRequest(orgId)
      );

      return aircraftData.items;
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return [];
      } else {
        throw error;
      }
    }
  }

  private getLatestTrackRequest(pointCount: number | undefined = undefined): Request {
    const request = new Request(
      this.buildUrl(
        `${this.getPath(
          'TRACKS'
        )}/latest?impersonateEmail=&pointsPerTrack=${pointCount ?? 10000}&fetchEvents=true&fetchDeviceConnection=true`
      ),
      {}
    );

    return this.addAuthToRequest(request);
  }

  private getTrackHistoryRequest({
                                   trackIds = '',
                                   page,
                                   resultsPerPage,
                                   aircraftIds = '',
                                   startTime,
                                   endTime,
                                   sos = '',
                                   zeroMinFlights = true,
                                   zeroDistanceFlights = true,
                                   serialNumber,
                                   bootcount
                                 }: TrackHistoryRequest): Request {
    const path = `${this.getPath(
      'TRACKS'
    )}?aircraftIds=${aircraftIds}&contacts=false&impersonateEmail=&startTime=${startTime}&endTime=${endTime}&page=${page}&resultsPerPage=${resultsPerPage}&sos=${sos}&zeroMinFlights=${zeroMinFlights}&zeroDistanceFlights=${zeroDistanceFlights}&trackIds=${trackIds}&serialNumber=${serialNumber}&bootcount=${bootcount}`;

    const request = new Request(this.buildUrl(path), {});
    return this.addAuthToRequest(request);
  }

  /**
   * This is a duplicate of getTrackHistoryRequest to support the new tracks endpoint for the history table.
   */
  private getHistoryTableTracksRequest(params: URLSearchParams): Request {
    // NOTE: avoid accidentally mucking up the existing querystring
    const search = new URLSearchParams(params.toString());

    // Avoid passing empty parameters to the API
    search.forEach((v, k) => v === '' && search.delete(k));

    // NOTE: translate a few values that, for historical reasons, have different names. Also, confusingly,
    //   - duration == true means zeroMinFlights == false
    //   - distance == true means zeroDistanceFlights == false
    if (search.get('duration') === 'true') {
      search.append('zeroMinFlights', 'false');
      search.delete('duration');
    }
    if (search.get('distance') === 'true') {
      search.append('zeroDistanceFlights', 'false');
      search.delete('distance');
    }

    // Times in ms
    const startTime = search.get('start');
    const endTime = search.get('end');
    if (startTime) {
      search.set('startTime', (Number(startTime) * 1000).toString());
      search.delete('start');
    }
    if (endTime) {
      search.set('endTime', (Number(endTime) * 1000).toString());
      search.delete('end');
    }

    // 0-index page numbers
    const page = search.get('page');
    if (page) {
      search.set('page', (Number(page) - 1).toString());
    }

    const path = `${this.getPath('TRACKS')}/history-table?${search.toString()}`;
    const request = new Request(this.buildUrl(path), {});
    return this.addAuthToRequest(request);
  }

  private getDownloadTracksRequest(downloadTracksRequest: DownloadTracksRequest): Request {
    let paramString = `trackIds=${downloadTracksRequest.trackIds}`;
    if (downloadTracksRequest.serialNumber && downloadTracksRequest.bootCount) {
      paramString = `serialNumber=${downloadTracksRequest.serialNumber}&bootcount=${downloadTracksRequest.bootCount}`;
    }

    paramString += `&exportType=${
      downloadTracksRequest.type
    }&includeFlightLogs=${downloadTracksRequest.includeFlightLogs.toString()}`;

    const path = `${this.getPath('TRACKS')}?${paramString}`;
    const request = new Request(this.buildUrl(path), {});

    return this.addAuthToRequest(request);
  }

  public async downloadTracks(downloadTracksRequest: DownloadTracksRequest) {
    try {
      await this.call<string>(this.getDownloadTracksRequest(downloadTracksRequest), {
        process: async response => {
          const string = await response.text();
          let fileExtension = downloadTracksRequest.type;
          let fileName = 'exportedTracks';
          if (downloadTracksRequest.type === 'csvsummary') {
            fileExtension = 'csv';
            fileName = fileName + 'Summary';
          }
          fileDownload(string, `${fileName}.${fileExtension}`);
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  // The API does not exist in NODE but exists in Go. What do we do?
  private deleteTracksRequest(trackIds: [string]): Request {
    const path = this.buildUrl(`${this.getPath('TRACKS')}`);
    const requestBody = {
      trackIds: trackIds
    };
    const request = new Request(path, {
      method: 'DELETE',
      headers: [['Content-Type', 'application/json']],
      body: JSON.stringify(requestBody)
    });
    return this.addAuthToRequest(request);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async deleteTracks(trackIds: [string]): Promise<any> {
    try {
      await this.call(this.deleteTracksRequest(trackIds));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  public async getLatestTracks(organisationId: string | undefined = undefined,pointCount: number | undefined = undefined): Promise<PrivateTrackData[]> {
    try {
      const trackData = await this.call<TracksAPIResponse<PrivateTrackData[]>>(
        this.getLatestTrackRequest(pointCount)
      );

      return trackData.items;
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return [];
      } else {
        throw error;
      }
    }
  }

  public async getTrackHistory(props: TrackHistoryRequest): Promise<TrackHistoryData> {
    try {
      return await this.call<TracksAPIResponse<PrivateTrackData[]>>(
        this.getTrackHistoryRequest(props)
      );
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return {
          items: [],
          _meta: { count: 0, total: 0 }
        };
      } else {
        throw error;
      }
    }
  }

  /**
   * This is a duplicate of getTrackHistory to support the new tracks endpoint for the history table.
   */
  public async getHistoryTableTracks(params: URLSearchParams): Promise<HistoryTableData> {
    try {
      const historyTracks = await this.call<HistoryTableTracksAPIResponse<PrivateTrackData[]>>(
        this.getHistoryTableTracksRequest(params)
      );
      if (
        window.env.ENV !== 'development' &&
        window.env.ENV !== 'local' &&
        window.env.ENV !== 'test'
      ) {
        return historyTracks;
      }
      // TODO remove this stub when PLA-1971 is done
      const stubPOILocations: POILocation[] = [
        {
          id: 1,
          name:
            "Los Angeles County Sheriff's Department Lester J White Search and Rescue Regional Training Facility Heliport"
        },
        {
          id: 2,
          GPSCode: '27PN',
          name: 'Lehigh Valley Hospital/Hecktown Oaks Heliport'
        },
        {
          id: 3,
          GPSCode: '0NM0',
          IATA: 'CUS',
          name: 'Columbus Airport'
        },
        {
          id: 4,
          GPSCode: 'EELM',
          ICAO: 'EELM',
          name: 'Lange Airfield (Lennundusmuuseum)'
        },
        {
          id: 5,
          GPSCode: '00AA',
          name: 'Aero B Ranch Airport'
        },
        {
          id: 6
        }
      ];
      const stubbedHistoryTracks = {
        ...historyTracks,
        items: historyTracks.items.map((item, index) => {
          return {
            ...item,
            flightLog:item.flightLog ? {
              ...item.flightLog,
              firstTakeoff:index % 3 != 0 ? {
                eventId: 'even-1',
                time: 1748437093971,
                type: 'FLIGHT',
                subtype: 'TAKEOFF',
                location: stubPOILocations[index % stubPOILocations.length],
              } : undefined,
              lastLanding:index % 2 == 1 ? {
                eventId: 'even-10',
                time: 1748438093971,
                type: 'FLIGHT',
                subtype: 'LANDING',
                location: stubPOILocations[(index + 1) % stubPOILocations.length] 
              } : undefined
            } : undefined
          };
        })
      };
      return stubbedHistoryTracks;
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return {
          errors: [],
          items: [],
          meta: { count: 0, total: 0 }
        };
      } else {
        throw error;
      }
    }
  }

  /**
   * The purpose of this function is to download the history table tracks as a CSV file.
   */
  public async getHistoryTableTracksCSV(params: URLSearchParams) {
    try {
      const search = new URLSearchParams(params.toString());
      // Avoid passing empty parameters to the API
      search.forEach((v, k) => v === '' && search.delete(k));

      const path = `${this.getPath('TRACKS_V2')}/summary-csv-for-filter?${search.toString()}`;
      const request = new Request(this.buildUrl(path), {});

      await this.call<string>(this.addAuthToRequest(request), {
        process: async response => {
          const string = await response.text();
          let fileName = 'exportedTracksSummary';
          fileDownload(string, `${fileName}.csv`);
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  public async getHistoryTableTracksTotals(params: URLSearchParams): Promise<TrackTotalsResponse> {
    try {
      const search = new URLSearchParams(params.toString());
      // Avoid passing empty parameters to the API
      search.forEach((v, k) => v === '' && search.delete(k));

      const path = `${this.getPath('TRACKS_V2')}/tracks-summary-totals?${search.toString()}`;
      const request = new Request(this.buildUrl(path), {});

      return await this.call<TrackTotalsResponse>(this.addAuthToRequest(request));
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return {
          totalDistanceMeters: 0,
          totalDurationMilliSeconds: 0
        };
      } else {
        throw error;
      }
    }
  }

  public async getKML(): Promise<KMLData[]> {
    let request = new Request(this.buildUrl(this.getPath('KML')), {});
    request = this.addAuthToRequest(request);
    const kmlData = await this.call<KMLAPIResponse>(request);

    return kmlData.items.map((item): KMLData => ({ ...item, type: 'kml' }));
  }

  public async getUsers(): Promise<UserData> {
    let request = new Request(this.buildUrl(this.getPath('USER')), {});
    request = this.addAuthToRequest(request);

    const userData = await this.call<UsersAPIResponse>(request);
    if (
      window.env.ENV !== 'development' &&
      window.env.ENV !== 'local' &&
      window.env.ENV !== 'test'
    ) {
      return userData;
    }
    // TODO: Remove this stub when PLA-1974 is done
    const stubUserData = {
      ...userData,
      orgs: userData.orgs.map(org => {
        return { ...org, org: { ...org.org, locationCodePreference: LocationCodeStandard.IATA } };
      })
    };
    return stubUserData;
  }

  public async getMembers(orgId: string): Promise<Member[]> {
    let request = new Request(
      this.buildUrl(this.getPath('MEMBERS').replace('<ORGANISATION_ID>', orgId)),
      {}
    );
    request = this.addAuthToRequest(request);

    return (await this.call<MembersAPIResponse>(request)).items;
  }

  public async getPersons(orgId: string): Promise<Person[]> {
    try {
      let request = new Request(
        this.buildUrl(this.getPath('PERSONS').replace('<ORGANISATION_ID>', orgId))
      );
      request = this.addAuthToRequest(request);

      return await this.call<PersonAPIResponse>(request);
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return [];
      } else {
        throw error;
      }
    }
  }

  public async getPersonAssociatedTags(orgId: string): Promise<PersonAssociatedTag[]> {
    try {
      let request = new Request(`${this.buildUrl(this.getPath('USER_TAGS'))}?orgId=${orgId}`, {});
      request = this.addAuthToRequest(request);

      const tagsData = await this.call<PersonAssociatedTagAPIResponse>(request);

      return tagsData.items;
    } catch (error) {
      if (this.convertErrorToEvent(error)) {
        return [];
      } else {
        throw error;
      }
    }
  }

  public async savePersonAssociatedTag(tag: SavePersonAssociatedTagParams): Promise<any> {
    try {
      const requestBody = { personId: tag.personId, macAddress: tag.macAddress };
      let request = new Request(`${this.buildUrl(this.getPath('USER_TAGS'))}`, {
        method: 'POST',
        headers: [['Content-Type', 'application/json']],
        body: JSON.stringify(requestBody)
      });
      request = this.addAuthToRequest(request);

      await this.call<{}>(request);
      return { success: true };
    } catch (error) {
      if (!this.convertErrorToEvent(error)) {
        throw error;
      }
    }
  }

  public async deletePersonAssociatedTag(tagId: number): Promise<any> {
    try {
      const requestBody = { id: tagId };
      let request = new Request(`${this.buildUrl(this.getPath('USER_TAGS'))}`, {
        method: 'DELETE',
        headers: [['Content-Type', 'application/json']],
        body: JSON.stringify(requestBody)
      });
      request = this.addAuthToRequest(request);

      await this.call<{}>(request);
    } catch (error) {
      if (!this.convertErrorToEvent(error)) {
        throw error;
      }
    }
  }

  private isEmpty(obj: object): boolean {
    if (!obj) {
      return true;
    }
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  public async saveUserPreference<K extends keyof UserPreference, V extends UserPreference[K]>(
    preferenceKey: K,
    preferenceValue: V
  ): Promise<void> {
    if (preferenceKey == undefined || this.isEmpty(preferenceValue)) {
      throw Error('User preference key or value is empty');
    }

    let url;
    let requestBody;

    switch (preferenceKey) {
      case UserPreferenceKey.GROUPING_OPTION:
      case UserPreferenceKey.CLUSTERING_OPTION:
      case UserPreferenceKey.SHOW_TRACKS:
      case UserPreferenceKey.AIRCRAFT_SORT:
      case UserPreferenceKey.MAP_LAYER:
        url = this.buildUrl(this.getPath('USER_PREFERENCES'));
        requestBody = JSON.stringify(preferenceValue);
        break;
      case UserPreferenceKey.KML_LAYER:
        let body = preferenceValue as UserPreference[UserPreferenceKey.KML_LAYER];
        url = this.buildUrl(this.getPath('KML_STATE') + body.selectedKml);
        requestBody = JSON.stringify({ isActive: body.isActive });
        break;
      default:
        throw Error('Unknown user preference type: ' + preferenceKey);
    }

    if (url && requestBody) {
      const request = new Request(url, {
        method: 'PUT',
        headers: [['Content-Type', 'application/json']],
        body: requestBody
      });

      await this.call(this.addAuthToRequest(request));

      return;
    }
  }

  public async getFeatureFlags(): Promise<FeatureFlagsAPIResponse> {
    let request = new Request(this.buildUrl(this.getPath('FEATURES')), {});
    request = this.addAuthToRequest(request);

    return await this.call<FeatureFlagsAPIResponse>(request);
  }

  public getFavouritesService(): FavouritesService {
    this.serviceInstances['FavouritesService'] ??= new FavouritesService(window.env.api.endpoint);

    return this.serviceInstances['FavouritesService'] as FavouritesService;
  }

  public getSpiderSettingsService(): SpiderSettingsService {
    this.serviceInstances['SpiderSettingsService'] ??= new SpiderSettingsService(window.env.api.endpoint);

    return this.serviceInstances['SpiderSettingsService'] as SpiderSettingsService;
  }

  public getIntegrationService(): IntegrationService {
    this.serviceInstances['IntegrationService'] ??= new IntegrationService(window.env.api.endpoint);

    return this.serviceInstances['IntegrationService'] as IntegrationService;
  }

  public getAircraftService(): AircraftService {
    this.serviceInstances['AircraftService'] ??= new AircraftService(window.env.api.endpoint);

    return this.serviceInstances['AircraftService'] as AircraftService;
  }

  public getFlightDataService(): FlightDataService {
    this.serviceInstances['FlightDataService'] ??= new FlightDataService(window.env.api.endpoint);

    return this.serviceInstances['FlightDataService'] as FlightDataService;
  }

  public getFlightData3DFRService(): FlightData3DFRService {
    this.serviceInstances['FlightData3DFRService'] ??= new FlightData3DFRService(window.env.newApi.endpoint);

    return this.serviceInstances['FlightData3DFRService'] as FlightData3DFRService;
  }

  public getGeofencesService(): GeofencesService {
    this.serviceInstances['GeofencesService'] ??= new GeofencesService(window.env.api.endpoint);

    return this.serviceInstances['GeofencesService'] as GeofencesService;
  }

  public getSisenseService(): SisenseService {
    this.serviceInstances['SisenseService'] ??= new SisenseService(window.env.api.endpoint);

    return this.serviceInstances['SisenseService'] as SisenseService;
  }

  public getFlightEventConfigurationsService(): FlightEventConfigurationsService {
    this.serviceInstances['FlightEventConfigurationsService'] ??= new FlightEventConfigurationsService(window.env.api.endpoint);

    return this.serviceInstances['FlightEventConfigurationsService'] as FlightEventConfigurationsService;
  }

  public getEventRulesService(): EventRulesService {
    this.serviceInstances['EventRulesService'] ??= new EventRulesService(window.env.api.endpoint);

    return this.serviceInstances['EventRulesService'] as EventRulesService;
  }

  public getInsightRulesService(): InsightRulesService {
    this.serviceInstances['InsightRulesService'] ??= new InsightRulesService(window.env.api.endpoint);

    return this.serviceInstances['InsightRulesService'] as InsightRulesService;
  }

  public getNotificationConfigurationService(): NotificationConfigurationService {
    this.serviceInstances['NotificationConfigurationService'] ??= new NotificationConfigurationService(window.env.newApi.endpoint);

    return this.serviceInstances['NotificationConfigurationService'] as NotificationConfigurationService;
  }

  public getEventService(): EventService {
    this.serviceInstances['EventService'] ??= new EventService(window.env.api.endpoint);

    return this.serviceInstances['EventService'] as EventService;
  }

  public getEventsExplorerService(): EventsExplorerService {
    this.serviceInstances['EventsExplorerService'] ??= new EventsExplorerService(window.env.newApi.endpoint);

    return this.serviceInstances['EventsExplorerService'] as EventsExplorerService;
  }

  public getFeatureConfigService(): FeatureConfigService {
    this.serviceInstances['FeatureConfigService'] ??= new FeatureConfigService(window.env.api.endpoint);

    return this.serviceInstances['FeatureConfigService'] as FeatureConfigService;
  }

  public getUserService(): UserService {
    this.serviceInstances['UserService'] ??= new UserService(window.env.api.endpoint);

    return this.serviceInstances['UserService'] as UserService;
  }

  public getContactService(): ContactService {
    this.serviceInstances['ContactService'] ??= new ContactService(window.env.api.endpoint);

    return this.serviceInstances['ContactService'] as ContactService;
  }

  public getConversationService(): ConversationService {
    this.serviceInstances['ConversationService'] ??= new ConversationService(window.env.api.endpoint);

    return this.serviceInstances['ConversationService'] as ConversationService;
  }
}
